/**
 * The comp eval pill and the calendar entry (Bobbie, 2026-11-02: "noted on
 * the calendar as a pill everywhere that week as dancers have to bring their
 * costumes on that day"). The words come from lib/compEvals.ts so every
 * surface says the same thing; this file only draws them.
 *
 * Renders nothing when there is nothing to say: no date, a different week,
 * or an eval already behind us.
 *
 * Reads nothing itself, so the dancer home may import it (its no-authority
 * rule, tests/dancer-session-client.test.mjs). The staff home's
 * self-loading line is ./StaffCompEvalWeekPills.tsx.
 */
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import {
  COMP_EVAL_COSTUME_LINE,
  COMP_EVAL_TITLE,
  compEvalPillText,
  compEvalWhenLabel,
  type CompEvalDate,
} from '../lib/compEvals';

const pillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 28,
  padding: '4px 10px',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--amber-bg)',
  border: '1px solid var(--amber-border)',
  color: 'var(--amber-text)',
  fontFamily: 'var(--font-body)',
  fontSize: 12.5,
  fontWeight: 800,
  lineHeight: 1.25,
  textDecoration: 'none',
};

export function CompEvalPill({
  entry,
  dayKey,
  todayKey,
  className,
  href,
  style,
}: {
  entry: CompEvalDate | null | undefined;
  /** The day the surface is about: today on a page, the meeting's day on a dated card. */
  dayKey: string;
  todayKey: string;
  /** Named when the pill sits somewhere that is not already this class. */
  className?: string;
  href?: string;
  style?: CSSProperties;
}) {
  if (!entry) return null;
  const text = compEvalPillText(entry, dayKey, todayKey);
  if (!text) return null;
  const label = className ? `${className}: ${text}` : text;
  const body = (
    <>
      <span aria-hidden="true">👗</span>
      <span>{label}</span>
    </>
  );
  const merged = { ...pillStyle, ...style };
  if (href) {
    return (
      <Link to={href} style={merged} data-comp-eval-pill={entry.offeringId}>
        {body}
      </Link>
    );
  }
  return (
    <span style={merged} data-comp-eval-pill={entry.offeringId}>
      {body}
    </span>
  );
}

/**
 * The pills for every class in a list that has an eval this week, for a home
 * screen or a page header. One line per class, at most `max`.
 */
export function CompEvalWeekPills({
  entries,
  classNames,
  todayKey,
  hrefFor,
  max = 4,
  style,
}: {
  entries: readonly CompEvalDate[];
  classNames: ReadonlyMap<string, string>;
  todayKey: string;
  hrefFor?: (offeringId: string) => string;
  max?: number;
  style?: CSSProperties;
}) {
  const showing = entries
    .filter((entry) => compEvalPillText(entry, todayKey, todayKey))
    .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))
    .slice(0, max);
  if (showing.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', ...style }} data-comp-eval-week>
      {showing.map((entry) => (
        <CompEvalPill
          key={entry.offeringId}
          entry={entry}
          dayKey={todayKey}
          todayKey={todayKey}
          className={classNames.get(entry.offeringId)}
          href={hrefFor?.(entry.offeringId)}
        />
      ))}
    </div>
  );
}

/**
 * One calendar day's evals, as entries (the StudioEventDayEntries shape):
 * the class, the time, and the costume line. Each opens the class.
 */
export function CompEvalDayEntries({
  entries,
  classNames,
  dateKey,
  hrefFor,
  compact,
}: {
  entries: readonly CompEvalDate[];
  classNames: ReadonlyMap<string, string>;
  dateKey: string;
  hrefFor?: (offeringId: string) => string;
  compact?: boolean;
}) {
  const showing = entries
    .filter((entry) => entry.date === dateKey && classNames.has(entry.offeringId))
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
  if (showing.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {showing.map((entry) => {
        const inner = (
          <>
            <span aria-hidden="true" style={{ fontSize: 15, flexShrink: 0 }}>👗</span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 800, fontSize: compact ? 13 : 13.5 }}>
                {COMP_EVAL_TITLE}: {classNames.get(entry.offeringId)}
              </span>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-soft)', marginTop: 1 }}>
                {compEvalWhenLabel(entry)}. {COMP_EVAL_COSTUME_LINE}
              </span>
            </span>
          </>
        );
        const style: CSSProperties = {
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 44,
          boxSizing: 'border-box',
          textDecoration: 'none',
          padding: compact ? '6px 10px' : '7px 11px',
          borderRadius: 'var(--radius-md)',
          background: 'var(--amber-bg)',
          borderLeft: '4px solid var(--amber-border)',
          color: 'var(--ink)',
          fontFamily: 'var(--font-body)',
        };
        const href = hrefFor?.(entry.offeringId);
        return href ? (
          <Link key={entry.offeringId} to={href} style={style} data-comp-eval-day={entry.offeringId}>
            {inner}
          </Link>
        ) : (
          <div key={entry.offeringId} style={style} data-comp-eval-day={entry.offeringId}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}
